// Shared low-poly tropic parts (tropical, dry & wetland trees) — `h01` is the
// deterministic jitter stream (never Math.random, so a variant always rebuilds
// byte-identical geometry); `tube` lays a tapered low-poly limb between two
// arbitrary points (trunks, forking branches, stilt roots — no hand-derived
// Euler angles at the call site); `blade` chains thin box segments into an
// arching, drooping leaf blade (palm fronds, fern fronds, banana paddles);
// `tuft` is the squashed foliage blob the broad canopies layer. Ported from
// age-of-mcp's v2/tropic-parts.ts: the pure geometry is kept verbatim, with the
// Part type taken from the library.

import * as THREE from "three";
import type { Part } from "../types";

export type Vec3 = readonly [number, number, number];

/** Deterministic 0..1 hash stream keyed by (seed, n). */
export function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 113.9 + n * 271.3 + 49.7) * 43758.5453;
  return x - Math.floor(x);
}

/** Linear blend between `a` and `b`. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * A tapered low-poly tube laid from `from` to `to` (radius `rFrom` at the
 * start, `rTo` at the end). Orientation solved with the XYZ-euler identity
 * R(rx, 0, rz) · ŷ = (−sin rz, cos rz·cos rx, cos rz·sin rx), so any
 * direction is reachable with two angles.
 */
export function tube(
  from: Vec3, to: Vec3, rFrom: number, rTo: number,
  color: THREE.ColorRepresentation, radial = 5,
): Part {
  const dx = to[0] - from[0], dy = to[1] - from[1], dz = to[2] - from[2];
  const len = Math.hypot(dx, dy, dz) || 1e-5;
  const rz = Math.asin(Math.max(-1, Math.min(1, -dx / len)));
  const rx = Math.atan2(dz / len, dy / len);
  return {
    geometry: new THREE.CylinderGeometry(rTo, rFrom, len, radial),
    color,
    position: [from[0] + dx / 2, from[1] + dy / 2, from[2] + dz / 2],
    rotation: [rx, 0, rz],
  };
}

export interface BladeOpts {
  /** Number of chained segments (2–3 reads as a smooth arch). */
  segs: number;
  /** Total blade length, root to tip. */
  len: number;
  /** Blade width at the root (tapers toward the tip). */
  width: number;
  /** Blade thickness. */
  thick: number;
  /** Pitch of the first segment above horizontal (radians, + = up). */
  rise: number;
  /** Pitch drop per segment — the arch / droop of the blade. */
  droop: number;
  /** Tip width as a fraction of root width (default 0.55). */
  taper?: number;
  /** Per-segment colours, cycled along the blade. */
  colors: readonly string[];
}

/**
 * An arching leaf blade pointing toward horizontal angle `yaw`: a chain of
 * thin box segments starting at `base` pitched `rise` above horizontal, each
 * following segment pitched `droop` lower, so the blade rises then breaks
 * over and droops — a palm frond, fern frond or banana paddle. A box rotated
 * [0, −yaw, pitch] (XYZ euler) points its long axis along
 * (cos yaw·cos pitch, sin pitch, sin yaw·cos pitch), staying flat (no roll).
 */
export function blade(base: Vec3, yaw: number, o: BladeOpts): Part[] {
  const parts: Part[] = [];
  const segLen = o.len / o.segs;
  const taper = o.taper ?? 0.55;
  let [px, py, pz] = base;
  let pitch = o.rise;
  for (let i = 0; i < o.segs; i++) {
    const t = o.segs > 1 ? i / (o.segs - 1) : 0;
    const w = lerp(o.width, o.width * taper, t);
    const dx = Math.cos(yaw) * Math.cos(pitch);
    const dy = Math.sin(pitch);
    const dz = Math.sin(yaw) * Math.cos(pitch);
    parts.push({
      geometry: new THREE.BoxGeometry(segLen, o.thick, w),
      color: o.colors[i % o.colors.length]!,
      position: [px + dx * segLen / 2, py + dy * segLen / 2, pz + dz * segLen / 2],
      rotation: [0, -yaw, pitch],
    });
    px += dx * segLen; py += dy * segLen; pz += dz * segLen;
    pitch -= o.droop;
  }
  return parts;
}

/** A squashed icosahedron foliage blob — the canopy-layer primitive.
 *  `r` is the horizontal radius; vertical extent ≈ 0.85·r·squash. */
export function tuft(
  x: number, y: number, z: number, r: number, squash: number,
  color: THREE.ColorRepresentation, ry = 0,
): Part {
  return {
    geometry: new THREE.IcosahedronGeometry(r, 0), color,
    position: [x, y, z], rotation: [0, ry, 0], scale: [1, squash, 1],
  };
}
